import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtDateShort } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { AbrirAppButton, MaintenanceOperationsPanel } from "../features/maintenance/MaintenanceOperationsPanel";
import { useMobile } from "../features/maintenance-mobile/mobile-ui";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { IconTruck, IconCog } from "../components/icons";
import type { TransportMaintenanceDashboardDto } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

export function MaintenancePage() {
  // Na casca mobile já se está no celular: "Abrir App" não leva a lugar nenhum novo.
  const mobile = useMobile();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading, isError: dataError, refetch: dataRefetch } = useQuery({
    queryKey: ["transport", "maintenance", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportMaintenanceDashboardDto>(
        `/dashboards/transport/maintenance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const s = data?.summary;
  const p = data?.prevSummary;
  const cmp = data?.comparisonLabel ?? "vs. mês anterior";

  const costDelta  = computeDelta(s?.cost,  p?.cost,  { direction: "higher_is_worse" });
  const countDelta = computeDelta(s?.count, p?.count, { direction: "neutral" });

  const byDay      = data?.byDay      ?? [];
  const costTrend  = data?.costTrend  ?? [];
  const byOrigin   = data?.byOrigin   ?? [];
  const topVehicles = data?.topVehicles ?? [];
  const vehicleList = useProgressiveList(topVehicles);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Custos de manutenção"
        subtitle="Operação (checklists e ocorrências) e custo das ordens de serviço do ERP"
        actions={mobile ? undefined : <AbrirAppButton />}
      />

      {dataError && <QueryErrorBanner onRetry={() => { dataRefetch(); }} />}

      {/* Operação — mesmo serviço que alimenta o início do Portal Operacional */}
      <MaintenanceOperationsPanel from={from} to={to} />

      <div className="pt-2">
        <h3 className="text-base font-semibold text-gray-900">Custo de manutenção (ERP)</h3>
        <p className="text-sm text-gray-500">
          Ordens de serviço do SinerSoft (oficina própria e terceiros). É outra grandeza: não se soma às O.S. do SIAG.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPIStatCard label="Gasto com Manutenção" memory={{ cardKey: "manutencao-erp.gasto", from, to, expected: s?.cost }} value={s ? fmtBRL(s.cost) : "—"}
          delta={costDelta} comparisonLabel={cmp}
          status={s && s.cost > 0 ? "warning" : "neutral"} loading={isLoading} />
        <KPIStatCard label="Ordens de Serviço" memory={{ cardKey: "manutencao-erp.os", from, to, expected: s?.count }} value={s ? fmtNumber(s.count) : "—"}
          delta={countDelta} comparisonLabel={cmp} status="neutral" loading={isLoading} />
        <KPIStatCard label="Custo Médio / OS" memory={{ cardKey: "manutencao-erp.custo-medio", from, to, expected: s?.avgCost }} value={s ? fmtBRL(s.avgCost) : "—"}
          comparisonLabel="no período" status="neutral" loading={isLoading} />
        <KPIStatCard label="Peças" memory={{ cardKey: "manutencao-erp.pecas", from, to, expected: s?.partsCost }} value={s ? fmtBRL(s.partsCost) : "—"}
          comparisonLabel="no período" status="neutral" loading={isLoading} />
        <KPIStatCard label="Mão de Obra" memory={{ cardKey: "manutencao-erp.mao-obra", from, to, expected: s?.laborCost }} value={s ? fmtBRL(s.laborCost) : "—"}
          comparisonLabel="no período" status="neutral" loading={isLoading} />
        <KPIStatCard label="Terceiros" memory={{ cardKey: "manutencao-erp.terceiros", from, to, expected: s?.externalCost }} value={s ? fmtBRL(s.externalCost) : "—"}
          comparisonLabel="vs. oficina própria"
          status={s && s.cost > 0 && s.externalCost / s.cost > 0.6 ? "warning" : "neutral"} loading={isLoading} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Gasto com manutenção por dia" subtitle="Valor no período" height={240}
          isEmpty={!isLoading && byDay.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byDay} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-maint" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--brand)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Gasto"]} labelFormatter={fmtDateShort}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Area type="monotone" dataKey="cost" stroke="var(--brand)" strokeWidth={2} fill="url(#grad-maint)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Evolução do gasto" subtitle="Por mês, nos últimos 5 meses" height={240}
          isEmpty={!isLoading && costTrend.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={costTrend} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Gasto"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Bar dataKey="cost" fill="var(--brand)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Ranking veículos + interna×externa */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconTruck className="w-4 h-4 text-gray-400" />
              Gasto com Manutenção por Veículo
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : topVehicles.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : vehicleList.visible.map((v, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{v.plate}</p>
                  <p className="text-xs text-gray-400">{fmtNumber(v.count)} OS</p>
                </div>
                <p className="text-sm font-semibold text-gray-900">{v.formatted}</p>
              </div>
            ))}
            {!isLoading && <LoadMoreBar list={vehicleList} label="veículos" />}
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconCog className="w-4 h-4 text-gray-400" />
              Oficina Própria × Terceiros
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : byOrigin.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : byOrigin.map((o, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{o.origin}</p>
                  <p className="text-xs text-gray-400">{fmtNumber(o.count)} OS</p>
                </div>
                <p className="text-sm font-semibold text-tm-700 flex-shrink-0">{o.formatted}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
